import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { authService, getErrorStatus, setUnauthorizedHandler, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  // Set when the stored session couldn't be checked because the server was unreachable (the token is kept).
  connectionError: boolean;
  retry: () => void;
  login: (email: string, password: string) => Promise<User>;
  // The new account: unless its email_confirmed_at is set, it can log in only after the emailed link (ACC-16).
  register: (email: string, fullName: string, password: string, role: 'teacher' | 'student', newsletterOptIn?: boolean,
             inviteCode?: string | null) => Promise<User>;
  logout: () => void;
  // After the user changed their own account (settings).
  setCurrentUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setConnectionError(false);
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        // Only an invalid or expired token ends the session; a network error or a server hiccup must not log
        // the user out (the interceptor already dropped the token on a 401).
        if (getErrorStatus(error) === 401) {
          authService.logout();
        } else {
          setConnectionError(true);
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    // Any authenticated request answered with 401: the token is gone, so is the user. ProtectedRoute then
    // sends them to /login?next=… and they come back to the same page after logging in.
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = async (email: string, password: string) => {
    await authService.login({ username: email, password });
    const userData = await authService.getCurrentUser();
    setConnectionError(false);
    setUser(userData);
    return userData;
  };

  const register = async (email: string, fullName: string, password: string, role: 'teacher' | 'student', newsletterOptIn = false,
                          inviteCode: string | null = null) => {
    return authService.register({ email, full_name: fullName, password, role, newsletter_opt_in: newsletterOptIn, invite_code: inviteCode });
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, connectionError, retry: loadUser, login, register, logout, setCurrentUser: setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
